/**
 * Detour analysis — pure computation layer, no browser dependencies.
 * Can be imported and tested in Node.js.
 *
 * OSRM Table API returns durations in seconds.
 * Matrix shapes:
 *   Request 1: sources=[start], dests=[POI₁…POIₙ] → 1×N or N (flattened)
 *   Request 2: sources=[POI₁…POIₙ], dests=[end]  → N×1 or N (flattened)
 */

import type { LatLng } from "./types";
import type { ParsedPoi } from "./poi";

export { type LatLng } from "./types";

// ─── Matrix extraction ────────────────────────────────────────────────────────

/**
 * Extract the duration for source s → dest d from a OSRM table response matrix.
 * OSRM returns sources × destinations shaped matrix, or a 1D array when one
 * dimension is 1.
 */
export function extractDuration(
  durations: (number | null)[][] | number[],
  sourceIdx: number,
  destIdx: number,
): number | null {
  // 1D array (one source OR one destination)
  if (!Array.isArray(durations[0])) {
    const arr = durations as number[];
    // sources=1: arr[i] = duration to dest i
    // dests=1:   arr[i] = duration from source i (implicitly source 0)
    // We need source/sourceIdx → dest/destIdx
    const isOneSource  = durations.length > 0 && Number.isFinite(durations[0] as number);
    // If all values are finite, assume 1D = [dest0, dest1, ...] with implicit source 0
    if (isOneSource) {
      // Check if this looks like a 1-source row or 1-dest column
      // Heuristic: if sourceIdx===0, return arr[destIdx], else null
      // For our use case we only ever pass sourceIdx=0 for 1-source requests
      return sourceIdx === 0 ? (arr[destIdx] ?? null) : null;
    }
    return null;
  }

  // 2D matrix
  const row = (durations as number[][])[sourceIdx];
  if (!row) return null;
  return row[destIdx] ?? null;
}

/**
 * Compute detour info for every POI given raw OSRM table response matrices.
 *
 * @param pois          - POI list (mutated in place)
 * @param toPoiDurations   - response from request 1: start → all POIs
 * @param fromPoiDurations  - response from request 2: all POIs → end
 * @param origDurS          - original start→end duration in seconds
 */
export function computeDetourCosts(
  pois: ParsedPoi[],
  toPoiDurations:   (number | null)[][] | number[],
  fromPoiDurations: (number | null)[][] | number[],
  origDurS: number,
): void {
  pois.forEach((poi, i) => {
    const arrivalRaw = extractDuration(toPoiDurations, 0, i);
    const departRaw = extractDuration(fromPoiDurations, i, 0);

    if (arrivalRaw == null || departRaw == null) {
      poi.detour = { arrivalS: -1, detourCostS: -1, reachable: false };
      return;
    }

    const arrival    = arrivalRaw as number;
    const depart     = departRaw as number;
    const detourCost = (arrival + depart) - origDurS;

    poi.detour = { arrivalS: arrival, detourCostS: detourCost, reachable: true };
  });
}

// ─── OSRM Table API ──────────────────────────────────────────────────────────

const OSRM_BASE = "https://router.project-osrm.org/table/v1/driving";

/** Build OSRM table URL. */
export function buildTableUrl(
  sources: LatLng[],
  dests:   LatLng[],
): string {
  const allCoords = [...sources, ...dests].map(p => `${p.lng.toFixed(6)},${p.lat.toFixed(6)}`);
  const srcIdx = sources.map((_, i) => i).join(";");
  const dstIdx = dests.map((_, i) => sources.length + i).join(";");
  return `${OSRM_BASE}/${allCoords.join(";")}?sources=${srcIdx}&destinations=${dstIdx}&annotations=duration`;
}

export interface TableResult {
  code:      string;
  durations: (number | null)[][];
}

/** Fetch one OSRM table response. */
export async function fetchTable(sources: LatLng[], dests: LatLng[]): Promise<TableResult> {
  const url = buildTableUrl(sources, dests);
  const res  = await fetch(url);
  if (!res.ok) throw new Error(`OSRM table error: ${res.status}`);
  const data = await res.json() as { code: string; durations: (number | null)[][] };
  if (data.code !== "Ok") throw new Error(`OSRM table: ${data.code}`);
  return { code: data.code, durations: data.durations };
}

/**
 * Fetch detour info for all POIs via two OSRM table requests.
 *
 * Request 1: sources=[start],  dests=[POI₁…POIₙ]
 * Request 2: sources=[POI₁…POIₙ], dests=[end]
 */
export async function fetchDetourInfo(
  pois:     ParsedPoi[],
  start:    LatLng,
  end:      LatLng,
  origDurS: number,
): Promise<void> {
  if (!pois.length) return;

  const [toResult, fromResult] = await Promise.all([
    fetchTable([start],          pois),
    fetchTable(pois.map(p => ({ lat: p.lat, lng: p.lng })), [end]),
  ]);

  computeDetourCosts(pois, toResult.durations, fromResult.durations, origDurS);
}
